import apiClient from './api';

// Önbellek mekanizması
const cache = {
  hotelList: new Map(),
  hotelDetail: new Map(),
  smallData: new Map()
};

// Cache key oluşturma yardımcı fonksiyonu
const createCacheKey = (params) => JSON.stringify(params);

// Hata loglama yardımcı fonksiyonu
const logError = (functionName, error) => {
  console.error(`Error in hotelService.${functionName}:`, error.message);
  if (error.response) {
    console.error('Status:', error.response.status);
    console.error('Data:', error.response.data);
  }
};

export const hotelService = {
  // Otel listesi getirme
  async getHotels(params) {
    const requestData = {
      ...params,
      // Eğer boards parametresi varsa ve doluysa ekle
      ...(params.boards && params.boards.length > 0 && {
        boards: {
          included: true,
          board: params.boards
        }
      }),
      // Eğer accommodations parametresi varsa ve doluysa ekle
      ...(params.accommodations && params.accommodations.length > 0 && {
        accommodations: params.accommodations
      })
    };
    const cacheKey = createCacheKey(requestData);
    
    // Önbellekte varsa, önbellekten getir
    if (cache.hotelList.has(cacheKey)) {
      return cache.hotelList.get(cacheKey);
    }
    
    try {
      console.log('Fetching hotel list with params:', requestData);
      console.log('Full request data:', JSON.stringify(requestData, null, 2));
      
      const response = await apiClient.post('hotel', requestData);
      const data = response.data.data;
      
      // null değerleri filtrele
      const filteredData = data.filter(hotel => hotel !== null);
      
      console.log('Hotel list data:', filteredData);
      // Sonucu önbelleğe kaydet
      cache.hotelList.set(cacheKey, filteredData);
      console.log(`Cached ${filteredData.length} hotels`);
      
      return filteredData;
    } catch (error) {
      logError('getHotels', error);
      throw error;
    }
  },
  
  // Küçük otel verisi getirme
  async getSmallData(codes, params) {
    const requestData = {
      codes,
      ...params,
      boards: params.boards || null
    };
    const cacheKey = createCacheKey(requestData);
    
    if (cache.smallData.has(cacheKey)) {
      return cache.smallData.get(cacheKey);
    }
    
    try {
      const response = await apiClient.post('hotel/small-data', requestData);
      const data = response.data.data;
      
      cache.smallData.set(cacheKey, data);
      
      return data;
    } catch (error) {
      logError('getSmallData', error);
      throw error;
    }
  },
  
  // Otel kodu ile otel getirme
  async getHotelWithCode(params) {
    const requestData = {
      ...params,
      boards: params.boards || null
    };
    const cacheKey = createCacheKey(requestData);
    
    // Önbellekte varsa, önbellekten getir
    if (cache.hotelDetail.has(cacheKey)) {
      console.log('Hotel detail cache hit');
      return cache.hotelDetail.get(cacheKey);
    }
    
    try {
      const response = await apiClient.post('hotel/with-code', requestData);
      console.log('Hotel detail response:', response);
      const data = response.data.data;
      
      // Sonucu önbelleğe kaydet
      cache.hotelDetail.set(cacheKey, data);
      
      return data;
    } catch (error) {
      logError('getHotelWithCode', error);
      throw error;
    }
  },
  
  // Önbelleği temizleme
  clearCache() {
    cache.hotelList.clear();
    cache.hotelDetail.clear();
    cache.smallData.clear();
    console.log('Hotel service cache cleared');
  },

  // Yeni otel arama fonksiyonu
  async searchHotels(params) {
    console.log('searchHotels params: ', params);
    const requestData = {
      geolocation: {
        latitude: params.geolocation?.latitude,
        longitude: params.geolocation?.longitude,
        radius: params.geolocation?.radius || 10
      },
      page: params.page || 1,
      perPage: params.perPage || 10,
      sourceMarket: params.sourceMarket || "TR",
      checkIn: params.checkIn,
      checkOut: params.checkOut,
      occupancies: params.occupancies || [{
        rooms: 1,
        adults: 2,
        children: 0,
        paxes: []
      }],
      boardCodes: params.boardCodes || [],
      accommodations: params.accommodations || [],
      chains: params.chains || [],
      filter: {
        ...(params.filter?.minCategory && params.filter?.maxCategory && { minCategory: params.filter?.minCategory, maxCategory: params.filter?.maxCategory }),
        ...(params.filter?.minRate && params.filter?.maxRate && { minRate: params.filter?.minRate, maxRate: params.filter?.maxRate })
      },
      ...(params.sortType && params.sortOrder && {
        sortOption:{
          type: params.sortType,
          column: params.sortOrder
        }
      }),
      ...(params.facilities && params.facilities.length > 0 && { amenities: params.facilities })
    };

    try {
      const response = await apiClient.post('hotels/search/hotels', requestData);
      return response.data;
    } catch (error) {
      logError('searchHotels', error);
      throw error;
    }
  },

  // Otel içerik bilgilerini getirme
  async getHotelContent(hotelId) {
    try {
      const response = await apiClient.post(`hotels/hotel-content/${hotelId}`);
      // API yanıtını kontrol et
      if (!response.data) {
        console.warn(`Empty response data for hotel ID: ${hotelId}`);
        return { hotel: {} };
      }
      if (!response.data.hotel) {
        console.warn(`No hotel data found for ID: ${hotelId}`);
        return { hotel: {} };
      }
      return response.data.hotel;
      
    } catch (error) {
      console.error(`getHotelContent error for hotel ID: ${hotelId}:`, error.message);
      if (error.response) {
        console.error('Status:', error.response.status);
        console.error('Data:', error.response.data);
      }
      // Hata durumunda da boş bir hotel objesi döndür
      return { hotel: {} };
    }
  },

  // Otel müsaitlik bilgilerini getirme
  async getHotelAvailability(params) {
    const requestData = {
      hotelId: params.hotelId,
      sourceMarket: params.sourceMarket || "DE",
      checkIn: params.checkIn,
      checkOut: params.checkOut,
      occupancies: params.occupancies || [{
        rooms: 1,
        adults: 2,
        children: 0,
        paxes: []
      }],
      boardCodes: params.boardCodes || [],
    };

    try {
      console.log('Fetching hotel availability with params:', requestData);
      const response = await apiClient.post('hotels/availability', requestData);
      return response.data;
    } catch (error) {
      logError('getHotelAvailability', error);
      throw error;
    }
  }
};

export default hotelService; 